import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Link2, Loader2, Truck } from 'lucide-react';
import type { Location } from '../types';
import { useLocationDelegation } from '../hooks/useLocationDelegation';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The owned location whose fulfilment is being delegated. */
  location: Location | null;
  /** Delegation targets — shared 3PL locations the org is an active client of. */
  sharedLocations: Location[];
  /**
   * BLU-367 claim-shadow — pre-select this target instead of the location's
   * current delegation when the dialog is opened from the "Delegate
   * fulfilment" shortcut in ConnectedVia3PLSection (the shared location the
   * shortcut was raised for). Ignored once the user picks a different target.
   */
  presetTargetId?: string | null;
}

/**
 * BLU-3PL — owner-side "Delegate fulfilment" dialog.
 *
 * Lets a 3PL org point one of its OWNED locations at a shared 3PL location it
 * claims as a client, so orders routed there are produced and fulfilled by the
 * partner while the owning org keeps order ownership. Backend contract lives in
 * useLocationDelegation.ts.
 *
 * States handled:
 *   - Empty: no shared locations connected yet → explain the claim-code path,
 *     Confirm disabled.
 *   - New: pick a target, Confirm enabled once chosen.
 *   - Already delegated: current target pre-selected; re-picking a different
 *     target enables Confirm ("Update delegation"), plus a reversible inline
 *     "Remove delegation" confirm (mirrors ConnectedVia3PLSection's revoke).
 *   - Bound: the location's Shopify id is the one a partner connection was
 *     claimed with, so the backend only accepts that partner's location. The
 *     target is locked to it and "Remove delegation" is hidden (BLU-1367).
 *
 * Per DESIGN.md §10 (no modal-from-modal stacking), the remove confirm is an
 * inline reveal inside this dialog, not a nested AlertDialog.
 */
function targetLabel(loc: Location): string {
  const owner = loc.owner_organization_name?.trim() || '3PL partner';
  return `${owner}: ${loc.name}`;
}

export default function DelegateFulfilmentDialog({
  open,
  onOpenChange,
  location,
  sharedLocations,
  presetTargetId = null,
}: Props) {
  const { setLocationDelegation } = useLocationDelegation();

  const currentDelegationId = location?.delegated_to_location_id ?? null;
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(currentDelegationId);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Reset local state whenever the dialog opens for a (possibly different) row.
  // presetTargetId takes priority over the current delegation — it's how the
  // claim-shadow "Delegate fulfilment" shortcut pre-fills the target.
  useEffect(() => {
    if (open) {
      setSelectedTargetId(presetTargetId ?? currentDelegationId);
      setConfirmingRemove(false);
    }
  }, [open, location?.id, currentDelegationId, presetTargetId]);

  const isPending = setLocationDelegation.isPending;
  const hasTargets = sharedLocations.length > 0;
  const isDelegated = currentDelegationId !== null;
  // The partner connection claimed with this location's Shopify id, if any.
  // Ids are compared as strings: a bigint can arrive as a number or a string.
  const bound =
    location?.shopify_location_id != null
      ? (sharedLocations.find(
          (s) =>
            s.client_shopify_location_id != null &&
            String(s.client_shopify_location_id) === String(location.shopify_location_id),
        ) ?? null)
      : null;
  const targetId = bound ? bound.id : selectedTargetId;
  // The previously-delegated target may no longer be a connected shared
  // location (junction revoked after delegation). Surface a note in that case.
  const currentTargetOrphaned =
    isDelegated && !sharedLocations.some((loc) => loc.id === currentDelegationId);

  const canConfirm =
    hasTargets && targetId !== null && targetId !== currentDelegationId && !isPending;

  const handleConfirm = () => {
    if (!location || !targetId) return;
    setLocationDelegation.mutate(
      { locationId: location.id, delegatedToLocationId: targetId },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const handleRemove = () => {
    if (!location) return;
    setLocationDelegation.mutate(
      { locationId: location.id, delegatedToLocationId: null },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-threadheads-purple" aria-hidden="true" />
            Delegate fulfilment
          </DialogTitle>
          <DialogDescription>
            Orders routed to {location?.name ?? 'this location'} will be produced and fulfilled at
            the selected 3PL location. Your organization keeps ownership of the orders.
          </DialogDescription>
          {/* BLU-670: delegation is a live IA switch — say so before the click.
              Once every location the org owns is delegated, the app
              reclassifies it (Production workspace hidden, /orders becomes the
              client inbox); removing the delegation restores the owner view. */}
          <p className="text-xs text-muted-foreground">
            If this is the only location your organization runs itself, delegating it switches the
            app to the client view: the Production workspace is hidden and Orders becomes your
            order inbox.{bound ? null : ' Removing the delegation switches it back.'}
          </p>
        </DialogHeader>

        {!hasTargets ? (
          // Empty state — nothing to delegate to yet.
          <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-6 text-center">
            <Link2 className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium">No 3PL locations connected yet</p>
            <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
              Connect to a 3PL provider using a claim code in the 3PL Connections section below,
              then you can delegate fulfilment here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <label htmlFor="delegate-target" className="text-sm font-medium">
              Fulfil at
            </label>
            <Select
              value={targetId ?? undefined}
              onValueChange={(v) => setSelectedTargetId(v)}
              disabled={isPending || bound !== null}
            >
              <SelectTrigger
                id="delegate-target"
                aria-label="Choose a 3PL location"
                aria-describedby={bound ? 'delegate-target-bound' : undefined}
              >
                <SelectValue placeholder="Choose a 3PL location" />
              </SelectTrigger>
              <SelectContent>
                {sharedLocations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {targetLabel(loc)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {bound ? (
              <p id="delegate-target-bound" className="text-xs text-muted-foreground">
                This location uses a Shopify location that is connected to a partner warehouse.
                Disconnect from the partner first, then change who fulfils it.
              </p>
            ) : (
              currentTargetOrphaned && (
                <p className="text-xs text-amber-700">
                  The 3PL location this was delegated to is no longer connected. Choose a new one,
                  or remove the delegation.
                </p>
              )
            )}
          </div>
        )}

        {/* Reversible remove affordance — only when a delegation is active
            and the backend would accept clearing it. */}
        {isDelegated && !bound && (
          <div className="border-t pt-4">
            {confirmingRemove ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Remove delegation? {location?.name ?? 'This location'} will produce and fulfil its
                  own orders again. You can delegate it again at any time.
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-orange-300 bg-transparent text-orange-700 hover:bg-orange-50 hover:text-orange-700"
                    onClick={handleRemove}
                    disabled={isPending}
                  >
                    {isPending ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : null}
                    Remove delegation
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmingRemove(false)}
                    disabled={isPending}
                  >
                    Keep delegation
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="text-orange-700 hover:bg-orange-50 hover:text-orange-700"
                onClick={() => setConfirmingRemove(true)}
                disabled={isPending}
              >
                Remove delegation
              </Button>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {isDelegated ? 'Update delegation' : 'Delegate fulfilment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
